/**
 * Version metadata catalog for SQL keywords / functions / syntax / clauses.
 *
 * Each entry binds a single (engineId, name) pair to its availability gate:
 *   - `addedIn` — first server version that ships the feature.
 *   - `deprecatedAtCompat` (MSSQL) — compatibility level that flags the
 *     feature as deprecated but still parses.
 *   - `removedAtCompat` (MSSQL) — compatibility level that no longer parses
 *     the feature.
 *
 * Multi-engine constructs (e.g. `RETURNING`, `MERGE`, `JSON_TABLE`) get one
 * entry per engine because the gating differs across dialects. Catalog
 * lookup is by `(engineId, name)`; matching is case-insensitive on `name`.
 *
 * Sourced from `Ralph/SqlGrammar/PRD.md` § 2.5 plus dialect docs:
 *   - MSSQL `compatibility_level` table at learn.microsoft.com.
 *   - PG release notes per major version.
 *   - MySQL "What is new in MySQL 8.0" reference page.
 *   - Oracle Database SQL Reference per release.
 *   - SQLite version log at sqlite.org/changes.html.
 */

import type { SqlEngine } from '@/features/sql-grammar/parseOptions';

export type VersionMetadataKind = 'keyword' | 'function' | 'syntax' | 'clause';

/**
 * One catalog entry. The version-format string is engine-specific:
 *   - mssql:    `'2016' | '2017' | '2019' | '2022'` (year)
 *   - postgres: `'10' | '11' | '12' | '13' | '14' | '15' | '16' | '17' | '19'`
 *               (major; PG19 adds SQL/PGQ property graphs — `GRAPH_TABLE`,
 *               `CREATE PROPERTY GRAPH`)
 *   - mysql:    `'8.0' | '8.0.16' | '8.0.23'` (semver-ish)
 *   - oracle:   `'11.2' | '12.1' | '12.2' | '18' | '19' | '21' | '23'`, plus
 *               23ai release-update (RU) granularity `'23.6' | '23.8' | '23.9'`
 *               for features that landed in a specific RU (e.g. SQL/PGQ)
 *   - sqlite:   `'3.24' | '3.25' | '3.31' | '3.35' | '3.37' | '3.45'`
 */
export type VersionMetadataEntry = {
  readonly name: string;
  readonly kind: VersionMetadataKind;
  readonly engineId: SqlEngine;
  readonly addedIn?: string;
  readonly deprecatedAtCompat?: number;
  readonly removedAtCompat?: number;
  readonly badge?: string;
};

const MSSQL: readonly VersionMetadataEntry[] = [
  // SQL Server 2012
  { engineId: 'mssql', name: 'CONCAT', kind: 'function', addedIn: '2012' },
  { engineId: 'mssql', name: 'FORMAT', kind: 'function', addedIn: '2012' },
  { engineId: 'mssql', name: 'IIF', kind: 'function', addedIn: '2012' },
  { engineId: 'mssql', name: 'LAG', kind: 'function', addedIn: '2012' },
  { engineId: 'mssql', name: 'LEAD', kind: 'function', addedIn: '2012' },
  { engineId: 'mssql', name: 'TRY_CONVERT', kind: 'function', addedIn: '2012' },
  { engineId: 'mssql', name: 'TRY_PARSE', kind: 'function', addedIn: '2012' },
  { engineId: 'mssql', name: 'THROW', kind: 'keyword', addedIn: '2012' },
  { engineId: 'mssql', name: 'DATEFROMPARTS', kind: 'function', addedIn: '2012' },
  { engineId: 'mssql', name: 'DATETIMEFROMPARTS', kind: 'function', addedIn: '2012' },
  { engineId: 'mssql', name: 'OFFSET', kind: 'clause', addedIn: '2012' },
  // SQL Server 2016
  { engineId: 'mssql', name: 'OPENJSON', kind: 'function', addedIn: '2016' },
  { engineId: 'mssql', name: 'JSON_VALUE', kind: 'function', addedIn: '2016' },
  { engineId: 'mssql', name: 'JSON_QUERY', kind: 'function', addedIn: '2016' },
  { engineId: 'mssql', name: 'JSON_MODIFY', kind: 'function', addedIn: '2016' },
  { engineId: 'mssql', name: 'STRING_SPLIT', kind: 'function', addedIn: '2016' },
  { engineId: 'mssql', name: 'SYSTEM_VERSIONING', kind: 'syntax', addedIn: '2016' },
  { engineId: 'mssql', name: 'COMPRESS', kind: 'function', addedIn: '2016' },
  { engineId: 'mssql', name: 'DECOMPRESS', kind: 'function', addedIn: '2016' },
  // SQL Server 2017
  { engineId: 'mssql', name: 'STRING_AGG', kind: 'function', addedIn: '2017' },
  { engineId: 'mssql', name: 'TRIM', kind: 'function', addedIn: '2017' },
  { engineId: 'mssql', name: 'TRANSLATE', kind: 'function', addedIn: '2017' },
  { engineId: 'mssql', name: 'CONCAT_WS', kind: 'function', addedIn: '2017' },
  // SQL Server 2019
  { engineId: 'mssql', name: 'APPROX_COUNT_DISTINCT', kind: 'function', addedIn: '2019' },
  // SQL Server 2022
  { engineId: 'mssql', name: 'JSON_OBJECT', kind: 'function', addedIn: '2022' },
  { engineId: 'mssql', name: 'JSON_ARRAY', kind: 'function', addedIn: '2022' },
  { engineId: 'mssql', name: 'GREATEST', kind: 'function', addedIn: '2022' },
  { engineId: 'mssql', name: 'LEAST', kind: 'function', addedIn: '2022' },
  { engineId: 'mssql', name: 'DATE_BUCKET', kind: 'function', addedIn: '2022' },
  { engineId: 'mssql', name: 'GENERATE_SERIES', kind: 'function', addedIn: '2022' },
  { engineId: 'mssql', name: 'IS DISTINCT FROM', kind: 'syntax', addedIn: '2022' },
  // Removed at compat 90: legacy `*=` / `=*` outer join.
  {
    engineId: 'mssql',
    name: '*=',
    kind: 'syntax',
    removedAtCompat: 90,
    badge: 'removed@90',
  },
  // Deprecated old RAISERROR(string-literal) form.
  {
    engineId: 'mssql',
    name: 'RAISERROR_LEGACY',
    kind: 'syntax',
    deprecatedAtCompat: 100,
    badge: 'deprecated',
  },
];

const POSTGRES: readonly VersionMetadataEntry[] = [
  // PG 9.x predates the catalog window — anything older than 10 is treated as
  // "always available" since Jam's lowest-supported PG is 10.
  { engineId: 'postgres', name: 'LATERAL', kind: 'keyword', addedIn: '9.3' },
  { engineId: 'postgres', name: 'FILTER', kind: 'clause', addedIn: '9.4' },
  { engineId: 'postgres', name: 'WITHIN GROUP', kind: 'clause', addedIn: '9.4' },
  { engineId: 'postgres', name: 'ON CONFLICT', kind: 'clause', addedIn: '9.5' },
  // PG 10
  { engineId: 'postgres', name: 'IDENTITY', kind: 'keyword', addedIn: '10' },
  // PG 11
  { engineId: 'postgres', name: 'CREATE PROCEDURE', kind: 'syntax', addedIn: '11' },
  { engineId: 'postgres', name: 'INCLUDE', kind: 'clause', addedIn: '11' },
  // PG 12
  { engineId: 'postgres', name: 'GENERATED ALWAYS AS', kind: 'syntax', addedIn: '12' },
  { engineId: 'postgres', name: 'jsonb_path_query', kind: 'function', addedIn: '12' },
  { engineId: 'postgres', name: 'jsonb_path_exists', kind: 'function', addedIn: '12' },
  // PG 13
  { engineId: 'postgres', name: 'gen_random_uuid', kind: 'function', addedIn: '13' },
  // PG 14
  { engineId: 'postgres', name: 'date_bin', kind: 'function', addedIn: '14' },
  // PG 15
  { engineId: 'postgres', name: 'MERGE', kind: 'keyword', addedIn: '15' },
  // PG 16
  { engineId: 'postgres', name: 'ANY_VALUE', kind: 'function', addedIn: '16' },
  // PG 17
  { engineId: 'postgres', name: 'MERGE...RETURNING', kind: 'syntax', addedIn: '17' },
  { engineId: 'postgres', name: 'JSON_TABLE', kind: 'function', addedIn: '17' },
  // PG 19 — SQL/PGQ (property graphs). PG19 ships the fixed-length-pattern
  // GRAPH_TABLE surface and the CREATE PROPERTY GRAPH DDL, but NONE of the
  // Oracle-style PGQ scalar functions (VERTEX_ID / MATCHNUM / … absent from
  // pg_proc — probe-pinned 2026-07-11, see `PG_PGQ_FUNCTIONS`), so there are
  // no per-function entries to gate here.
  { engineId: 'postgres', name: 'GRAPH_TABLE', kind: 'clause', addedIn: '19' },
  { engineId: 'postgres', name: 'CREATE PROPERTY GRAPH', kind: 'syntax', addedIn: '19' },
];

const MYSQL: readonly VersionMetadataEntry[] = [
  // MySQL 5.7
  { engineId: 'mysql', name: 'JSON_OBJECT', kind: 'function', addedIn: '5.7' },
  { engineId: 'mysql', name: 'JSON_ARRAY', kind: 'function', addedIn: '5.7' },
  { engineId: 'mysql', name: 'JSON_EXTRACT', kind: 'function', addedIn: '5.7' },
  // MySQL 8.0 baseline
  { engineId: 'mysql', name: 'WITH', kind: 'keyword', addedIn: '8.0' },
  { engineId: 'mysql', name: 'WINDOW', kind: 'clause', addedIn: '8.0' },
  { engineId: 'mysql', name: 'OVER', kind: 'clause', addedIn: '8.0' },
  { engineId: 'mysql', name: 'ROW_NUMBER', kind: 'function', addedIn: '8.0' },
  { engineId: 'mysql', name: 'RANK', kind: 'function', addedIn: '8.0' },
  { engineId: 'mysql', name: 'DENSE_RANK', kind: 'function', addedIn: '8.0' },
  { engineId: 'mysql', name: 'JSON_TABLE', kind: 'function', addedIn: '8.0' },
  { engineId: 'mysql', name: 'GROUPING', kind: 'function', addedIn: '8.0' },
  // MySQL 8.0.14
  { engineId: 'mysql', name: 'LATERAL', kind: 'keyword', addedIn: '8.0.14' },
  // MySQL 8.0.16
  { engineId: 'mysql', name: 'CHECK', kind: 'clause', addedIn: '8.0.16' },
  // MySQL 8.0.18
  { engineId: 'mysql', name: 'EXPLAIN ANALYZE', kind: 'syntax', addedIn: '8.0.18' },
  // MySQL 8.0.23
  { engineId: 'mysql', name: 'INVISIBLE', kind: 'keyword', addedIn: '8.0.23' },
  // MySQL 8.0.27
  { engineId: 'mysql', name: 'INTERSECT', kind: 'keyword', addedIn: '8.0.31' },
  { engineId: 'mysql', name: 'EXCEPT', kind: 'keyword', addedIn: '8.0.31' },
];

const ORACLE: readonly VersionMetadataEntry[] = [
  // Oracle 11g R2
  { engineId: 'oracle', name: 'LISTAGG', kind: 'function', addedIn: '11.2' },
  // Oracle 12c R1
  { engineId: 'oracle', name: 'IDENTITY', kind: 'keyword', addedIn: '12.1' },
  { engineId: 'oracle', name: 'FETCH FIRST', kind: 'clause', addedIn: '12.1' },
  { engineId: 'oracle', name: 'OFFSET', kind: 'clause', addedIn: '12.1' },
  { engineId: 'oracle', name: 'JSON_TABLE', kind: 'function', addedIn: '12.1' },
  { engineId: 'oracle', name: 'JSON_VALUE', kind: 'function', addedIn: '12.1' },
  { engineId: 'oracle', name: 'JSON_QUERY', kind: 'function', addedIn: '12.1' },
  { engineId: 'oracle', name: 'LATERAL', kind: 'keyword', addedIn: '12.1' },
  // Oracle 12c R2
  { engineId: 'oracle', name: 'APPROX_COUNT_DISTINCT', kind: 'function', addedIn: '12.2' },
  { engineId: 'oracle', name: 'LISTAGG_DISTINCT', kind: 'function', addedIn: '12.2' },
  // Oracle 18c
  { engineId: 'oracle', name: 'POLYMORPHIC TABLE', kind: 'syntax', addedIn: '18' },
  // Oracle 19c — SQL macros
  { engineId: 'oracle', name: 'SQL_MACRO', kind: 'function', addedIn: '19' },
  // Oracle 21c — JSON datatype
  { engineId: 'oracle', name: 'JSON datatype', kind: 'syntax', addedIn: '21' },
  // Oracle 23c
  { engineId: 'oracle', name: 'BOOLEAN', kind: 'keyword', addedIn: '23' },
  { engineId: 'oracle', name: 'GROUP BY ALL', kind: 'clause', addedIn: '23' },
  // SQL/PGQ (property graphs) — RU gates per the research memo §1 (probe-pinned)
  { engineId: 'oracle', name: 'GRAPH_TABLE', kind: 'clause', addedIn: '23' },
  { engineId: 'oracle', name: 'CREATE PROPERTY GRAPH', kind: 'syntax', addedIn: '23' },
  { engineId: 'oracle', name: 'VERTEX_ID', kind: 'function', addedIn: '23' },
  { engineId: 'oracle', name: 'EDGE_ID', kind: 'function', addedIn: '23' },
  { engineId: 'oracle', name: 'VERTEX_EQUAL', kind: 'function', addedIn: '23' },
  { engineId: 'oracle', name: 'EDGE_EQUAL', kind: 'function', addedIn: '23' },
  { engineId: 'oracle', name: 'BINDING_COUNT', kind: 'function', addedIn: '23.6' },
  { engineId: 'oracle', name: 'ONE ROW PER', kind: 'clause', addedIn: '23.7' },
  { engineId: 'oracle', name: 'MATCHNUM', kind: 'function', addedIn: '23.8' },
  { engineId: 'oracle', name: 'ELEMENT_NUMBER', kind: 'function', addedIn: '23.8' },
  { engineId: 'oracle', name: 'PATH_NAME', kind: 'function', addedIn: '23.9' },
  { engineId: 'oracle', name: 'PROPERTY_EXISTS', kind: 'function', addedIn: '23.9' },
];

const SQLITE: readonly VersionMetadataEntry[] = [
  // SQLite 3.24
  { engineId: 'sqlite', name: 'UPSERT', kind: 'syntax', addedIn: '3.24' },
  { engineId: 'sqlite', name: 'ON CONFLICT', kind: 'clause', addedIn: '3.24' },
  // SQLite 3.25
  { engineId: 'sqlite', name: 'OVER', kind: 'clause', addedIn: '3.25' },
  { engineId: 'sqlite', name: 'WINDOW', kind: 'clause', addedIn: '3.25' },
  { engineId: 'sqlite', name: 'ROW_NUMBER', kind: 'function', addedIn: '3.25' },
  { engineId: 'sqlite', name: 'RANK', kind: 'function', addedIn: '3.25' },
  { engineId: 'sqlite', name: 'DENSE_RANK', kind: 'function', addedIn: '3.25' },
  // SQLite 3.31
  { engineId: 'sqlite', name: 'GENERATED ALWAYS AS', kind: 'syntax', addedIn: '3.31' },
  // SQLite 3.32
  { engineId: 'sqlite', name: 'IIF', kind: 'function', addedIn: '3.32' },
  // SQLite 3.33
  { engineId: 'sqlite', name: 'UPDATE FROM', kind: 'syntax', addedIn: '3.33' },
  // SQLite 3.35
  { engineId: 'sqlite', name: 'RETURNING', kind: 'clause', addedIn: '3.35' },
  { engineId: 'sqlite', name: 'DROP COLUMN', kind: 'syntax', addedIn: '3.35' },
  // SQLite 3.37
  { engineId: 'sqlite', name: 'STRICT', kind: 'keyword', addedIn: '3.37' },
  // SQLite 3.38 — math built-ins
  { engineId: 'sqlite', name: 'pi', kind: 'function', addedIn: '3.38' },
  { engineId: 'sqlite', name: 'sqrt', kind: 'function', addedIn: '3.38' },
  // SQLite 3.45 — JSONB
  { engineId: 'sqlite', name: 'jsonb', kind: 'function', addedIn: '3.45' },
  { engineId: 'sqlite', name: 'jsonb_object', kind: 'function', addedIn: '3.45' },
];

const ALL_ENTRIES: readonly VersionMetadataEntry[] = [
  ...MSSQL,
  ...POSTGRES,
  ...MYSQL,
  ...ORACLE,
  ...SQLITE,
];

const BY_ENGINE: Readonly<Record<SqlEngine, ReadonlyMap<string, VersionMetadataEntry>>> = {
  mssql: indexByName(MSSQL),
  postgres: indexByName(POSTGRES),
  mysql: indexByName(MYSQL),
  oracle: indexByName(ORACLE),
  sqlite: indexByName(SQLITE),
};

function indexByName(
  entries: readonly VersionMetadataEntry[],
): ReadonlyMap<string, VersionMetadataEntry> {
  const map = new Map<string, VersionMetadataEntry>();
  for (const e of entries) {
    map.set(e.name.toLowerCase(), e);
  }
  return map;
}

export function getAllVersionMetadata(): readonly VersionMetadataEntry[] {
  return ALL_ENTRIES;
}

export function getVersionMetadata(
  engineId: SqlEngine,
  name: string,
): VersionMetadataEntry | undefined {
  return BY_ENGINE[engineId].get(name.toLowerCase());
}

export function getVersionMetadataForEngine(
  engineId: SqlEngine,
): readonly VersionMetadataEntry[] {
  switch (engineId) {
    case 'mssql':
      return MSSQL;
    case 'postgres':
      return POSTGRES;
    case 'mysql':
      return MYSQL;
    case 'oracle':
      return ORACLE;
    case 'sqlite':
      return SQLITE;
  }
}
